import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { authFetch, API } from '../api';

// Orders → Bring & HRX (guided P&P phase 7, 2026-10-17; backend/hrx_schedule.py). Fernando: "put a Bring & HRX button in
// the menu under Orders and it's highlighted when Bring can be sent today". Today's answer and why: the HRX ship (from
// the weekly e-mail), our HRX days, Skip / Go, and the parcels per country (packed + in open rounds + what fits before
// "goods ready"). Skip / Go / Mark sent need the HRX right.

interface Country { country: string; packed: number; in_rounds: number; forecast: number; total: number }
interface Decision {
  date: string; go: boolean; eligible: boolean; day_type: string; day_text: string; source: string;
  verified: boolean; source_text: string; ship_time: string | null; terminal_time: string | null;
  ready: string | null; order_by: string | null; override: { action: string; by_user: string; reason: string | null } | null;
  reasons: string[]; meets: string[]; min_per_country: number; min_countries: number;
  counts: { countries: Country[]; capacity: number; packing_window: string[] | null; wait_workdays: number; since: string | null; since_known: boolean };
}

const day = (iso: string, o: Intl.DateTimeFormatOptions) => new Date(`${iso}T12:00:00`).toLocaleDateString([], o);

// The sidebar's highlight: every 5 minutes (the server caches the decision 5 min) and right after Skip / Go / Undo here.
export function useBringGo(enabled: boolean): boolean {
  const [go, setGo] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const load = () => authFetch(`${API}/bring-hrx/today`).then(r => (r.ok ? r.json() : null))
      .then(d => { if (alive && d) setGo(!!d.today?.go); }).catch(() => {});
    load();
    const t = setInterval(load, 300000);
    window.addEventListener('wms-bring-hrx', load);
    return () => { alive = false; clearInterval(t); window.removeEventListener('wms-bring-hrx', load); };
  }, [enabled]);
  return go;
}

// The BringNO customs invoice (phase 9d, backend/customs_invoice.py): the template's codes valued at purchase price, the 3
// highest go on the invoice. Download only — nothing is sent from here.
function CustomsInvoiceCard() {
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [shipDate, setShipDate] = useState(() => new Date().toLocaleDateString('sv-SE'));
  const [r, setR] = useState<any>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [showOff, setShowOff] = useState(false);
  const [bulk, setBulk] = useState('');            // an HRX shipment's Norway boxes instead of the time window
  // a chosen window says so (window=1): with nothing chosen the server takes the open / just-left HRX shipment
  const qs = (extra = '', b = bulk) => (b ? `bulk=${encodeURIComponent(b)}` : `window=1&start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`) + extra;
  const calc = async (first = false, b = bulk) => {
    setErr(''); setBusy(true);
    try {
      const res = await authFetch(`${API}/customs/bring-no?${first ? '' : qs('', b)}`);
      const j = await res.json().catch(() => ({} as any));
      if (!res.ok) { setErr(j.detail || `HTTP ${res.status}`); return; }
      setR(j); setBulk(j.bulk || '');
      if (j.start) setStart(j.start);
      if (j.end) setEnd(j.end);
    } finally { setBusy(false); }
  };
  useEffect(() => { calc(true); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const download = async (confirmMissing = false) => {
    setErr('');
    const res = await authFetch(`${API}/customs/bring-no.xlsx?${qs(`&ship_date=${shipDate}${confirmMissing ? '&confirm_missing=true' : ''}`)}`);
    if (res.status === 409) {
      const j = await res.json().catch(() => ({} as any));
      if (window.confirm(`${j.detail}. Make the invoice anyway?`)) download(true);
      return;
    }
    if (!res.ok) { const j = await res.json().catch(() => ({} as any)); setErr(j.detail || `HTTP ${res.status}`); return; }
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url; a.download = `${shipDate.replace(/-/g, '')}.xlsx`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };
  const eur = (v: number) => v.toFixed(2);
  return (
    <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-5">
      <div className="text-gray-400 text-xs uppercase tracking-widest font-semibold mb-3">BringNO customs invoice</div>
      {r && r.bulks?.some((b: any) => b.parcels > 0) && (
        <div className="flex flex-wrap gap-2 mb-3 text-sm">
          {r.bulks.filter((b: any) => b.parcels > 0).map((b: any) => (
            <button key={b.code} onClick={() => calc(false, b.code)}
              className={`px-3 py-1.5 rounded border ${bulk === b.code ? 'border-blue-400 bg-blue-900/40 text-white' : 'border-gray-700 text-gray-300 hover:border-gray-500'}`}>
              {b.code}'s Norway boxes ({b.parcels}){b.status === 'open' ? ' — still open' : ''}</button>
          ))}
          <button onClick={() => calc(false, '')} className={`px-3 py-1.5 rounded border ${!bulk ? 'border-blue-400 bg-blue-900/40 text-white' : 'border-gray-700 text-gray-300 hover:border-gray-500'}`}>
            A time window</button>
        </div>
      )}
      {!bulk && <div className="flex flex-wrap items-end gap-3 text-sm text-gray-300">
        <label>Processed from<input type="datetime-local" value={start} onChange={e => setStart(e.target.value)}
          className="block mt-1 px-2 py-1 rounded bg-[#0f1117] border border-gray-700 text-white" /></label>
        <label>until<input type="datetime-local" value={end} onChange={e => setEnd(e.target.value)}
          className="block mt-1 px-2 py-1 rounded bg-[#0f1117] border border-gray-700 text-white" /></label>
        <button onClick={() => calc()} disabled={busy} className="px-3 py-1.5 rounded bg-gray-700 hover:bg-gray-600 text-white disabled:opacity-40">
          {busy ? 'Calculating…' : 'Calculate'}</button>
      </div>}
      {err && <div className="text-red-400 text-sm mt-2">{err}</div>}
      {r && (
        <div className="mt-4 text-sm">
          <div className="text-gray-400 mb-2">{r.orders.length} BringNO order(s) {r.bulk ? `in ${r.bulk}'s Norway boxes` : 'processed in the window'}. The {r.keep} highest codes go on the invoice.</div>
          <table className="w-full max-w-2xl">
            <thead><tr className="text-left text-gray-500 text-xs"><th className="py-1">Code</th><th>Goods</th><th>Origin</th><th>Pieces</th><th className="text-right">EUR</th><th /></tr></thead>
            <tbody>{r.rows.map((x: any) => (
              <tr key={x.code} className={`border-t border-gray-800 ${x.kept ? 'text-gray-100' : 'text-gray-500'}`}>
                <td className="py-1 font-mono">{x.code}</td><td>{x.description}</td><td>{x.origin}</td><td>{x.pcs}</td>
                <td className="text-right">{eur(x.value)}</td><td className="pl-3 text-emerald-300">{x.kept ? 'on the invoice' : ''}</td></tr>))}
              <tr className="border-t border-gray-700 font-bold text-gray-100"><td colSpan={4} className="py-1">Invoice total</td><td className="text-right">{eur(r.total)}</td><td /></tr>
            </tbody>
          </table>
          {r.off_invoice.length > 0 && (
            <div className="mt-2 text-xs text-gray-400">
              Not on the invoice (another code or none): {r.off_invoice.length} line(s), {eur(r.off_invoice_value)} EUR{' '}
              <button onClick={() => setShowOff(!showOff)} className="underline">{showOff ? 'hide' : 'show'}</button>
              {showOff && <ul className="mt-1 space-y-0.5">{r.off_invoice.map((x: any, i: number) => (
                <li key={i}>#{x.order} · {x.name} × {x.qty} · {x.code || 'no code'} · {x.value == null ? 'no price' : eur(x.value)}</li>))}</ul>}
            </div>
          )}
          {(r.missing_price.length > 0 || r.unmatched.length > 0) && (
            <div className="mt-2 text-xs text-amber-300">
              {r.missing_price.map((x: any, i: number) => <div key={'p' + i}>No purchase price: #{x.order} {x.name} ({x.code}) — not counted</div>)}
              {r.unmatched.map((x: any, i: number) => <div key={'u' + i}>No product in the WMS: #{x.order} {x.name} — not counted</div>)}
            </div>
          )}
          {r.not_found.length > 0 && (
            <div className="mt-2 text-xs text-amber-300">
              {r.not_found.map((x: any, i: number) => <div key={'n' + i}>Marked “can't find” at packing: #{x.order} {x.name} × {x.qty} — {x.went ? 'found and packed later, so it is counted' : 'never packed, so it is not on the invoice'}</div>)}
            </div>
          )}
          <div className="flex flex-wrap items-end gap-3 mt-4 text-sm text-gray-300">
            <label>Shipping date<input type="date" value={shipDate} onChange={e => setShipDate(e.target.value)}
              className="block mt-1 px-2 py-1 rounded bg-[#0f1117] border border-gray-700 text-white" /></label>
            <button onClick={() => download()} disabled={!r.kept.length}
              className="px-4 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white font-semibold disabled:opacity-40">
              Download {shipDate.replace(/-/g, '')}.xlsx</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function BringHrxPage() {
  const [d, setD] = useState<{ today: Decision; next: { date: string; type: string; text: string; eligible: boolean; source: string; verified: boolean;
    source_text: string; ready: string | null; ship_time: string | null }[]; can_skip: boolean } | null>(null);
  const [err, setErr] = useState('');
  const load = () => authFetch(`${API}/bring-hrx/today`).then(async r => {
    const j = await r.json().catch(() => ({} as any));
    if (!r.ok) { setErr(j.detail || `HTTP ${r.status}`); return; }
    setD(j); setErr('');
  }).catch(() => setErr('Could not load'));
  useEffect(() => { load(); }, []);
  const act = async (path: string, body: any = {}) => {
    const r = await authFetch(`${API}/bring-hrx/${path}`, { method: 'POST', body: JSON.stringify(body) });
    const j = await r.json().catch(() => ({} as any));
    if (!r.ok) { setErr(j.detail || `HTTP ${r.status}`); return; }
    load();
    window.dispatchEvent(new Event('wms-bring-hrx'));
  };
  if (!d) return <div className="p-6 text-gray-400">{err || 'Loading…'}</div>;
  const t = d.today;
  return (
    <div className="p-6 max-w-4xl space-y-6">
      <h1 className="text-2xl font-bold text-white">Bring & HRX</h1>
      {err && <div className="text-red-400">{err}</div>}
      <div className={`rounded-2xl border-2 p-6 ${t.go ? 'border-emerald-600 bg-emerald-900/15' : 'border-gray-700 bg-[#1a1d27]'}`}>
        <div className={`text-3xl font-black ${t.go ? 'text-emerald-200' : 'text-gray-200'}`}>
          {t.go ? 'Bring goes with HRX today' : t.eligible ? 'Bring waits today' : 'No HRX today'}</div>
        <div className="text-gray-300 mt-1">{day(t.date, { weekday: 'long', day: 'numeric', month: 'numeric' })} ·
          {' '}{t.day_text}{t.ship_time ? ` (ship ${t.ship_time}${t.terminal_time ? `, Tabasalu ${t.terminal_time}` : ''})` : ''}{' '}
          {t.verified ? <span className="text-xs text-emerald-400">✓ verified · {t.source_text}</span>
            : <span className="text-xs text-gray-500">{t.source_text}</span>}</div>
        {t.go && <div className="text-emerald-100 text-lg mt-2">Bring parcels ready by <b>{t.ready}</b>{t.order_by ? <> · book HRX by <b>{t.order_by}</b></> : null}</div>}
        {t.reasons.length > 0 && <ul className="mt-3 text-sm text-gray-300 list-disc list-inside">{t.reasons.map((r, i) => <li key={i}>{r}</li>)}</ul>}
        {d.can_skip && (
          <div className="flex flex-wrap gap-2 mt-4">
            {!t.override && t.eligible && <button onClick={() => { const r = window.prompt('Skip HRX today — why?'); if (r !== null) act('skip', { reason: r }); }}
              className="px-4 py-2 rounded-lg bg-amber-700 hover:bg-amber-600 text-white text-sm font-semibold">Skip HRX today</button>}
            {!t.override && !t.go && t.day_type !== 'no_ship' && <button onClick={() => { const r = window.prompt('Send with HRX today anyway — why?'); if (r !== null) act('go', { reason: r }); }}
              className="px-4 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-white text-sm font-semibold">Go anyway</button>}
            {t.override && <button onClick={() => act('undo')} className="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-sm">
              Undo {t.override.action} ({t.override.by_user})</button>}
            <button onClick={() => { if (window.confirm('HRX left now? Bring parcels processed before now no longer count as waiting.')) act('mark-sent'); }}
              className="px-4 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-sm">Mark HRX as sent now</button>
          </div>
        )}
      </div>

      <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-5">
        <div className="text-gray-400 text-xs uppercase tracking-widest font-semibold mb-3">
          Parcels per country (needs {t.min_per_country}+ in {t.min_countries}+ countries)</div>
        <table className="text-sm w-full max-w-xl">
          <thead><tr className="text-left text-gray-500 text-xs"><th className="py-1">Country</th><th>Packed, waiting</th><th>In open rounds</th><th>Will be packed before {t.ready || '—'}</th><th>Total</th></tr></thead>
          <tbody>{t.counts.countries.map(c => (
            <tr key={c.country} className={`border-t border-gray-800 ${t.meets.includes(c.country) ? 'text-emerald-300' : 'text-gray-300'}`}>
              <td className="py-1 font-semibold">Bring{c.country}</td><td>{c.packed}</td><td>{c.in_rounds}</td><td>{c.forecast}</td><td className="font-bold">{c.total}</td></tr>))}
          </tbody>
        </table>
        <div className="text-xs text-gray-500 mt-2">
          {t.counts.packing_window ? `Packing today ${t.counts.packing_window[0]}–${t.counts.packing_window[1]}: room for ${t.counts.capacity} more orders before ${t.ready}. `
            : 'No packing hours today (Settings → HRX & Bring) — nothing is forecast. '}
          {t.counts.since_known ? `Waiting = processed since the last HRX send.` : 'The last HRX send is not known yet — waiting counts the last 24 hours; press "Mark HRX as sent now" after a send.'}
          {t.counts.wait_workdays > 0 ? ` The oldest has waited ${t.counts.wait_workdays} workday(s).` : ''}</div>
      </div>

      {d.can_skip && <CustomsInvoiceCard />}

      <div className="bg-[#1a1d27] rounded-xl border border-gray-800 p-5">
        <div className="flex items-baseline justify-between mb-3">
          <div className="text-gray-400 text-xs uppercase tracking-widest font-semibold">Next days</div>
          <Link to="/settings/hrx-schedule" className="text-xs text-blue-400 hover:underline">HRX schedule →</Link>
        </div>
        {d.next.map(n => (
          <div key={n.date} className="text-sm text-gray-300 flex flex-wrap gap-x-3">
            <span className="w-28">{day(n.date, { weekday: 'short', day: 'numeric', month: 'numeric' })}</span>
            <span className={n.eligible ? 'text-gray-100' : 'text-gray-500'}>{n.text}{n.eligible && n.ready ? ` · Bring ready by ${n.ready}` : ''}{n.eligible ? '' : ' — not an HRX day'}</span>
            {n.verified ? <span className="text-xs text-emerald-400">✓ verified · {n.source_text}</span>
              : <span className="text-xs text-gray-500">{n.source === 'assumed' ? 'assumed' : n.source_text}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}
